import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, CircularProgress, Avatar, makeStyles, Divider, Chip
} from '@material-ui/core';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import SendIcon from '@material-ui/icons/Send';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { sendDirectNotification } from '../../services/userService';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    minWidth: 540,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  userHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(16,185,129,0.08)' : '#f0fdf4',
    border: '1px solid rgba(16,185,129,0.2)',
    marginBottom: theme.spacing(2.5),
  },
  avatar: {
    width: 46,
    height: 46,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  imagePreview: {
    width: '100%',
    height: 140,
    borderRadius: 10,
    objectFit: 'cover',
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  sendBtn: {
    borderRadius: 10,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    fontWeight: 600,
    padding: '8px 22px',
    boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  fcmBadge: {
    fontSize: '0.75rem',
    height: 24,
    fontWeight: 600,
  }
}));

export default function DirectNotificationDialog({ open, user, onClose, onSuccess }) {
  const classes = useStyles();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [buttonText, setButtonText] = useState('View Details');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError('Title and Body are required.');
      return;
    }

    setSending(true);
    setError('');
    try {
      await sendDirectNotification(user.id, {
        title: title.trim(),
        body: body.trim(),
        imageUrl: imageUrl.trim() || null,
        actionUrl: actionUrl.trim() || null,
        buttonText: buttonText.trim() || null,
        user,
      });

      // Reset form
      setTitle('');
      setBody('');
      setImageUrl('');
      setActionUrl('');
      setButtonText('View Details');

      if (onSuccess) {
        onSuccess(`Direct notification sent to ${user.displayName || user.name || 'user'}!`);
      }
      onClose();
    } catch (err) {
      console.error('Failed to send direct notification:', err);
      setError('Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const hasFcm = Boolean(user.fcmToken);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth="sm" fullWidth>
      <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12 }}>
        <NotificationsActiveIcon style={{ color: '#10b981' }} />
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Send Direct Push Notification
        </Typography>
      </DialogTitle>
      <Divider />

      <DialogContent style={{ paddingTop: 20 }}>
        {/* User Card */}
        <div className={classes.userHeader}>
          <Avatar className={classes.avatar}>
            <PersonOutlineIcon />
          </Avatar>
          <Box flex={1}>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {user.displayName || user.name || 'Anonymous User'}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.82rem' }}>
              {user.email || 'No email associated'} • ID: {user.id ? `${user.id.substring(0, 10)}...` : '-'}
            </Typography>
          </Box>
          <Chip
            icon={hasFcm ? <CheckCircleOutlineIcon style={{ fontSize: 16 }} /> : <ErrorOutlineIcon style={{ fontSize: 16 }} />}
            label={hasFcm ? 'FCM Device Token Active' : 'No FCM Token yet'}
            size="small"
            className={classes.fcmBadge}
            style={{
              background: hasFcm ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)',
              color: hasFcm ? '#10b981' : '#ef4444',
              border: `1px solid ${hasFcm ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            }}
          />
        </div>

        {error && (
          <Box mb={2} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.2)">
            <Typography style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        )}

        <form id="direct-notif-form" onSubmit={handleSend}>
          <TextField
            label="Notification Title *"
            fullWidth
            variant="outlined"
            size="small"
            className={classes.textField}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Important update regarding your account"
            required
          />

          <TextField
            label="Message Body *"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            className={classes.textField}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write the personalized message to this user..."
            required
          />

          <TextField
            label="Image URL (optional)"
            fullWidth
            variant="outlined"
            size="small"
            className={classes.textField}
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/banner.jpg"
          />
          {imageUrl && imageUrl.trim() && (
            <img
              src={imageUrl.trim()}
              alt="Preview"
              className={classes.imagePreview}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}

          <Box display="flex" style={{ gap: 16 }}>
            <TextField
              label="Action URL (optional)"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.textField}
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
              placeholder="e.g. /quran, /feedback, or https://..."
              helperText="Target route or external URL"
            />
            <TextField
              label="Button Text"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.textField}
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder="e.g. Read Now"
            />
          </Box>
        </form>
      </DialogContent>

      <Divider />
      <DialogActions style={{ padding: '14px 24px' }}>
        <Button onClick={onClose} disabled={sending} color="default">
          Cancel
        </Button>
        <Button
          type="submit"
          form="direct-notif-form"
          variant="contained"
          className={classes.sendBtn}
          startIcon={<SendIcon />}
          disabled={sending || !title.trim() || !body.trim()}
        >
          {sending ? <CircularProgress size={20} color="inherit" /> : 'Send to User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
